// frontend/src/pages/SupportPage.jsx
import { GlobalStyles } from '@bigcommerce/big-design';
import {
  ShoppingCart,
  DollarSign,
  Percent,
  Layers,
  Tag,
  Users,
  Folder,
  Monitor,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Menu,
  X,
  Zap,
  CreditCard,
} from 'lucide-react';
import { useState, useEffect } from 'react';

const colors = {
  indigo50: '#eef2ff',
  indigo100: '#e0e7ff',
  indigo600: '#4f46e5',
  indigo900: '#312e81',
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray800: '#1f2937',
  gray900: '#111827',
  green500: '#22c55e',
  white: '#ffffff',
};

const styles = {
  page: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    color: colors.gray900,
    backgroundColor: colors.white,
    margin: '-2rem',
    minHeight: '100vh',
  },
  // Navigation
  nav: {
    position: 'absolute',
    top: '2rem',
    left: 0,
    right: 0,
    zIndex: 50,
    display: 'flex',
    justifyContent: 'center',
    padding: '0 2rem',
  },
  navInner: {
    display: 'flex',
    width: '100%',
    maxWidth: '80rem',
    padding: '1rem 1.5rem',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  navLink: {
    color: 'rgba(255,255,255,0.9)',
    textDecoration: 'none',
    transition: 'color 0.2s',
    cursor: 'pointer',
  },
  navRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
  },
  navButton: {
    background: colors.white,
    color: colors.gray900,
    padding: '0.625rem 1.5rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    transition: 'transform 0.2s',
    textDecoration: 'none',
  },
  hamburger: {
    display: 'none',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.5rem',
  },
  mobileMenu: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: colors.white,
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    padding: '2rem',
  },
  mobileMenuHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '3rem',
  },
  mobileMenuClose: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.5rem',
  },
  mobileMenuLinks: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  mobileMenuLink: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: colors.gray900,
    textDecoration: 'none',
    padding: '0.75rem 0',
    borderBottom: `1px solid ${colors.gray100}`,
  },
  mobileMenuButton: {
    background: `linear-gradient(to right, ${colors.indigo600}, #9333ea)`,
    color: colors.white,
    padding: '1rem 2rem',
    borderRadius: '9999px',
    fontSize: '1rem',
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    marginTop: '2rem',
    textAlign: 'center',
    textDecoration: 'none',
  },
  header: {
    background: `linear-gradient(to right, ${colors.indigo600}, #9333ea)`,
    padding: '10rem 2rem 4rem 2rem',
    color: colors.white,
    position: 'relative',
  },
  headerContainer: {
    maxWidth: '64rem',
    margin: '0 auto',
  },
  headerTitle: {
    fontSize: '2.5rem',
    fontWeight: 700,
    margin: 0,
    marginBottom: '0.5rem',
  },
  headerSubtitle: {
    fontSize: '1.125rem',
    opacity: 0.9,
    margin: 0,
  },
  content: {
    maxWidth: '64rem',
    margin: '0 auto',
    padding: '3rem 2rem',
  },
  section: {
    marginBottom: '3rem',
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: colors.gray900,
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  sectionIcon: {
    width: '2.5rem',
    height: '2.5rem',
    background: `linear-gradient(to right, ${colors.indigo600}, #9333ea)`,
    borderRadius: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    background: colors.white,
    border: `1px solid ${colors.gray200}`,
    borderRadius: '0.75rem',
    padding: '1.5rem',
    marginBottom: '1rem',
  },
  cardTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: colors.gray900,
    marginBottom: '0.75rem',
  },
  cardText: {
    color: colors.gray600,
    lineHeight: 1.6,
    margin: 0,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1rem',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '1rem',
    background: colors.gray50,
    borderRadius: '0.5rem',
  },
  featureIcon: {
    width: '1.5rem',
    height: '1.5rem',
    color: colors.indigo600,
    flexShrink: 0,
    marginTop: '0.125rem',
  },
  featureText: {
    margin: 0,
    fontSize: '0.9375rem',
    color: colors.gray700,
  },
  featureTitle: {
    fontWeight: 600,
    color: colors.gray900,
    display: 'block',
    marginBottom: '0.5rem',
  },
  accordion: {
    border: `1px solid ${colors.gray200}`,
    borderRadius: '0.75rem',
    overflow: 'hidden',
    marginBottom: '0.75rem',
  },
  accordionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.5rem',
    background: colors.white,
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left',
  },
  accordionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.gray900,
    margin: 0,
  },
  accordionContent: {
    padding: '0 1.5rem 1.5rem',
    color: colors.gray600,
    lineHeight: 1.7,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '1rem',
  },
  th: {
    textAlign: 'left',
    padding: '0.75rem 1rem',
    background: colors.gray50,
    borderBottom: `1px solid ${colors.gray200}`,
    fontSize: '0.875rem',
    fontWeight: 600,
    color: colors.gray700,
  },
  td: {
    padding: '0.75rem 1rem',
    borderBottom: `1px solid ${colors.gray100}`,
    fontSize: '0.875rem',
    color: colors.gray600,
  },
  code: {
    background: colors.gray100,
    padding: '0.125rem 0.375rem',
    borderRadius: '0.25rem',
    fontSize: '0.875rem',
    fontFamily: 'monospace',
  },
  footer: {
    padding: '3rem 1.5rem',
    background: colors.white,
    borderTop: `1px solid ${colors.gray200}`,
  },
  footerContainer: {
    maxWidth: '80rem',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2rem',
  },
  footerLinks: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: colors.gray600,
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerLink: {
    color: colors.gray600,
    textDecoration: 'none',
    transition: 'color 0.2s',
  },
  footerDot: {
    color: colors.gray300,
  },
  footerCopyright: {
    fontSize: '0.75rem',
    color: colors.gray400,
    textAlign: 'center',
    margin: 0,
  },
};

function Accordion({ title, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div style={styles.accordion}>
      <button
        style={styles.accordionHeader}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span style={styles.accordionTitle}>{title}</span>
        {isOpen ? (
          <ChevronUp size={20} color={colors.gray500} />
        ) : (
          <ChevronDown size={20} color={colors.gray500} />
        )}
      </button>
      {isOpen && <div style={styles.accordionContent}>{children}</div>}
    </div>
  );
}

function SupportPage() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <GlobalStyles />
      <div style={styles.page}>
        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div style={styles.mobileMenu}>
            <div style={styles.mobileMenuHeader}>
              <img src="/logo-checkout-fees.png" alt="Checkout Fees" style={{ height: '42px' }} />
              <button style={styles.mobileMenuClose} onClick={closeMobileMenu}>
                <X size={28} color={colors.gray900} />
              </button>
            </div>
            <div style={styles.mobileMenuLinks}>
              <a href="/#features" style={styles.mobileMenuLink} onClick={closeMobileMenu}>Features</a>
              <a href="/#how-it-works" style={styles.mobileMenuLink} onClick={closeMobileMenu}>How It Works</a>
              <a href="/#use-cases" style={styles.mobileMenuLink} onClick={closeMobileMenu}>Use Cases</a>
              <a href="/support" style={styles.mobileMenuLink} onClick={closeMobileMenu}>Documentation</a>
            </div>
            <a href="/support#contact" style={styles.mobileMenuButton} onClick={closeMobileMenu}>Support</a>
          </div>
        )}

        {/* Header */}
        <header style={{
          ...styles.header,
          ...(isMobile && { padding: '8rem 3rem 3rem 3rem' })
        }}>
          {/* Navigation */}
          <nav style={styles.nav}>
            <div style={styles.navInner}>
              <a href="/">
                <img src="/logo-checkout-light.png" alt="Checkout Fees" style={{ height: '42px' }} />
              </a>

              {/* Desktop Navigation */}
              {!isMobile && (
                <>
                  <div style={styles.navLinks}>
                    <a href="/#features" style={styles.navLink}>Features</a>
                    <a href="/#how-it-works" style={styles.navLink}>How It Works</a>
                    <a href="/#use-cases" style={styles.navLink}>Use Cases</a>
                  </div>
                  <div style={styles.navRight}>
                    <a href="/support" style={styles.navLink}>Documentation</a>
                    <a href="/support#contact" style={styles.navButton}>Support</a>
                  </div>
                </>
              )}

              {/* Mobile Hamburger */}
              {isMobile && (
                <button style={{ ...styles.hamburger, display: 'block' }} onClick={() => setMobileMenuOpen(true)}>
                  <Menu size={28} color={colors.white} />
                </button>
              )}
            </div>
          </nav>

          <div style={styles.headerContainer}>
            <img src="/logo-checkout-light.png" alt="Checkout Fees" style={{ height: '48px', display: 'block', margin: '0 auto 1rem auto' }} />
            <h1 style={{
              ...styles.headerTitle,
              textAlign: 'center',
              ...(isMobile && { fontSize: '1.75rem' })
            }}>Documentation & Support</h1>
            <p style={{ ...styles.headerSubtitle, textAlign: 'center' }}>
              Learn how to configure and use Checkout Fees for your BigCommerce store
            </p>
          </div>
        </header>

        {/* Content */}
        <main style={{
          ...styles.content,
          ...(isMobile && { padding: '2rem 3rem' })
        }}>
          {/* Getting Started */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <ShoppingCart size={20} color={colors.white} />
              </div>
              Getting Started
            </h2>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Installation</h3>
              <p style={styles.cardText}>
                Install Checkout Fees from the BigCommerce App Marketplace and it's ready to go immediately, no setup step required. New installs default to Native Checkout, which delivers fees straight to BigCommerce's own standard checkout with nothing extra to configure.
              </p>
              <ol style={{ ...styles.cardText, marginTop: '1rem', paddingLeft: '1.25rem' }}>
                <li style={{ marginBottom: '0.5rem' }}>Open the Checkout Fees app from your BigCommerce dashboard</li>
                <li style={{ marginBottom: '0.5rem' }}>You're taken straight to the fee management page, already connected to your checkout</li>
                <li>Create your first fee and it appears in checkout automatically, no extra install step needed</li>
              </ol>
              <p style={{ ...styles.cardText, marginTop: '1rem' }}>
                Every new install includes a 14-day free trial with no card required. See "Free Trial & Billing" below.
              </p>
            </div>
          </section>

          {/* Checkout Integration */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <Zap size={20} color={colors.white} />
              </div>
              Checkout Integration
            </h2>
            <p style={{ ...styles.cardText, marginBottom: '1.5rem' }}>
              Checkout Fees delivers fees to your storefront in one of two ways. You can switch between them anytime from the app's Settings page, and your fee configurations stay the same either way.
            </p>
            <div style={{
              ...styles.grid,
              ...(isMobile && { gridTemplateColumns: '1fr' })
            }}>
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Native Checkout (Recommended, Default)</h3>
                <p style={styles.cardText}>
                  Uses BigCommerce's own standard checkout page, unmodified. The app detects cart changes automatically in the background and tells BigCommerce which fees apply; BigCommerce's checkout displays them natively, just like shipping or tax. No custom script to maintain, and no separate installation step.
                </p>
              </div>
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Custom Checkout (Advanced)</h3>
                <p style={styles.cardText}>
                  Installs a custom-built checkout page hosted by this app instead of BigCommerce's standard one. Only worth using if you need deeper checkout customization beyond fees; it requires more upkeep and doesn't receive BigCommerce's checkout updates as automatically. Most stores don't need this.
                </p>
              </div>
            </div>
          </section>

          {/* Free Trial & Billing */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <CreditCard size={20} color={colors.white} />
              </div>
              Free Trial & Billing
            </h2>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>14-Day Free Trial</h3>
              <p style={styles.cardText}>
                Every new install starts a 14-day free trial automatically, no credit card required. You can create and manage fees and use every feature during this period at no cost.
              </p>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Subscribing</h3>
              <p style={styles.cardText}>
                You can subscribe anytime from Settings. If you subscribe before your trial ends, you won't be charged until your free trial period is actually over. Billing runs through your BigCommerce account, the subscription appears as a line item on your regular BigCommerce invoice, not a separate charge.
              </p>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Cancelling</h3>
              <p style={styles.cardText}>
                You can cancel anytime from Settings. Fees keep working through the end of the period you already paid for; there's no refund for the unused remainder, but you also won't be charged again after cancelling.
              </p>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>What happens if my trial ends without subscribing?</h3>
              <p style={styles.cardText}>
                Fee management and delivery to checkout pause until you subscribe. Nothing is deleted; your existing fees and settings are preserved and pick back up automatically as soon as you subscribe.
              </p>
            </div>
          </section>

          {/* Fee Types */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <DollarSign size={20} color={colors.white} />
              </div>
              Fee Types
            </h2>
            <div style={{
              ...styles.grid,
              ...(isMobile && { gridTemplateColumns: '1fr' })
            }}>
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Fixed Amount Fees</h3>
                <p style={styles.cardText}>
                  Charge a flat dollar amount regardless of order value. Perfect for handling fees, processing charges, or flat-rate surcharges.
                </p>
                <p style={{ ...styles.cardText, marginTop: '0.75rem' }}>
                  <strong>Example:</strong> $5.00 handling fee, $2.99 processing fee
                </p>
              </div>
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Percentage-Based Fees</h3>
                <p style={styles.cardText}>
                  Charge a percentage of the order value. Scales automatically with cart size. Ideal for payment processing surcharges or commission-based fees.
                </p>
                <p style={{ ...styles.cardText, marginTop: '0.75rem' }}>
                  <strong>Example:</strong> 3% credit card fee, 10% service charge
                </p>
              </div>
            </div>
          </section>

          {/* Percentage Calculation Bases */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <Percent size={20} color={colors.white} />
              </div>
              Percentage Calculation Options
            </h2>
            <div style={styles.card}>
              <p style={styles.cardText}>
                When creating a percentage-based fee, you can choose what the percentage is calculated on:
              </p>
              <div style={isMobile ? { overflowX: 'auto' } : {}}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Calculation Base</th>
                    <th style={styles.th}>Description</th>
                    <th style={styles.th}>Example (10% fee)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={styles.td}><strong>Subtotal Excluding Tax</strong></td>
                    <td style={styles.td}>Product prices only, before tax</td>
                    <td style={styles.td}>$100 subtotal = $10.00 fee</td>
                  </tr>
                  <tr>
                    <td style={styles.td}><strong>Subtotal Including Tax</strong></td>
                    <td style={styles.td}>Product prices plus tax</td>
                    <td style={styles.td}>$109 (incl. tax) = $10.90 fee</td>
                  </tr>
                  <tr>
                    <td style={styles.td}><strong>Grand Total</strong></td>
                    <td style={styles.td}>Subtotal + shipping + tax</td>
                    <td style={styles.td}>$129 total = $12.90 fee</td>
                  </tr>
                  <tr>
                    <td style={styles.td}><strong>Amount Due</strong></td>
                    <td style={styles.td}>Grand total minus discounts/coupons</td>
                    <td style={styles.td}>$119 after coupon = $11.90 fee</td>
                  </tr>
                  <tr>
                    <td style={styles.td}><strong>Applicable Products (Default)</strong></td>
                    <td style={styles.td}>Based on product's default price × quantity</td>
                    <td style={styles.td}>$50 product × 2 = $10.00 fee</td>
                  </tr>
                  <tr>
                    <td style={styles.td}><strong>Applicable Products (Sale)</strong></td>
                    <td style={styles.td}>Based on product's sale price × quantity</td>
                    <td style={styles.td}>$40 sale price × 2 = $8.00 fee</td>
                  </tr>
                  <tr>
                    <td style={styles.td}><strong>Applicable Products (MSRP)</strong></td>
                    <td style={styles.td}>Based on product's MSRP × quantity</td>
                    <td style={styles.td}>$60 MSRP × 2 = $12.00 fee</td>
                  </tr>
                </tbody>
              </table>
              </div>
            </div>
          </section>

          {/* Application Modes */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <Layers size={20} color={colors.white} />
              </div>
              Application Modes
            </h2>
            <div style={{
              ...styles.grid,
              ...(isMobile && { gridTemplateColumns: '1fr' })
            }}>
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Per Order</h3>
                <p style={styles.cardText}>
                  The fee is applied once per order, regardless of how many items are in the cart.
                </p>
                <p style={{ ...styles.cardText, marginTop: '0.75rem' }}>
                  <strong>Example:</strong> $10 handling fee stays $10 whether the customer orders 1 item or 10 items.
                </p>
              </div>
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Per Product</h3>
                <p style={styles.cardText}>
                  The fee is multiplied by the total quantity of applicable products in the cart.
                </p>
                <p style={{ ...styles.cardText, marginTop: '0.75rem' }}>
                  <strong>Example:</strong> $2 per-item fee × 5 items = $10 total fee.
                </p>
              </div>
            </div>
          </section>

          {/* Targeting Options */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <Tag size={20} color={colors.white} />
              </div>
              Targeting & Restrictions
            </h2>
            <p style={{ ...styles.cardText, marginBottom: '1.5rem' }}>
              Fees can be targeted to specific products, categories, brands, channels, or customer groups. When multiple restrictions are applied, ALL conditions must be met (AND logic).
            </p>
            <div style={{
              ...styles.grid,
              ...(isMobile && { gridTemplateColumns: '1fr' })
            }}>
              <div style={styles.featureItem}>
                <Folder size={20} style={styles.featureIcon} />
                <p style={styles.featureText}>
                  <span style={styles.featureTitle}>Category Restrictions</span>
                  Apply fees only to products in specific categories (e.g., imported goods, hazmat items)
                </p>
              </div>
              <div style={styles.featureItem}>
                <Tag size={20} style={styles.featureIcon} />
                <p style={styles.featureText}>
                  <span style={styles.featureTitle}>Product Restrictions</span>
                  Apply fees to specific products only (e.g., oversized items, special orders)
                </p>
              </div>
              <div style={styles.featureItem}>
                <ShoppingCart size={20} style={styles.featureIcon} />
                <p style={styles.featureText}>
                  <span style={styles.featureTitle}>Brand Restrictions</span>
                  Apply fees to products from specific brands (e.g., vendor chargebacks)
                </p>
              </div>
              <div style={styles.featureItem}>
                <Monitor size={20} style={styles.featureIcon} />
                <p style={styles.featureText}>
                  <span style={styles.featureTitle}>Channel Restrictions</span>
                  Apply fees to specific sales channels (e.g., web only, marketplace only)
                </p>
              </div>
              <div style={styles.featureItem}>
                <Users size={20} style={styles.featureIcon} />
                <p style={styles.featureText}>
                  <span style={styles.featureTitle}>Customer Group Restrictions</span>
                  Apply fees to specific customer groups (e.g., wholesale, retail, VIP)
                </p>
              </div>
              <div style={styles.featureItem}>
                <CheckCircle size={20} style={styles.featureIcon} />
                <p style={styles.featureText}>
                  <span style={styles.featureTitle}>Combined Restrictions</span>
                  Combine multiple restrictions - all conditions must be met for fee to apply
                </p>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <CheckCircle size={20} color={colors.white} />
              </div>
              Frequently Asked Questions
            </h2>

            <Accordion title="How do fees appear to customers?" defaultOpen={true}>
              <p>
                Fees display as native line items in the checkout summary, just like shipping and tax. They appear with the display name you configure and are included in the order total. Fees are permanently recorded in order emails and order history, perfect for accounting, tax reporting, and customer records.
              </p>
            </Accordion>

            <Accordion title="Is there a limit to how many fees I can create?">
              <p>
                You can create unlimited fees in the app. However, BigCommerce has a limit of 5 surcharges displayed in checkout. If you have more than 5 fees, the additional fees will be automatically consolidated into a "Combined Additional Fees" line item. The total calculation remains accurate.
              </p>
            </Accordion>

            <Accordion title="Do fees persist through checkout?">
              <p>
                Yes! Fees persist through all checkout steps including shipping selection, payment, and order confirmation. They also persist if the customer refreshes the page or navigates back and forth through checkout.
              </p>
            </Accordion>

            <Accordion title="Can I apply fees to specific products or categories?">
              <p>
                Yes. You can restrict fees to specific products, categories, brands, sales channels, or customer groups. When a fee has restrictions, it acts as a "gate" - the fee only applies when at least one matching product is in the cart. If using "Applicable Products" percentage base, the fee is calculated only on matching products.
              </p>
            </Accordion>

            <Accordion title="How do percentage fees work with discounts?">
              <p>
                It depends on the calculation base you choose. If you select "Amount Due," the percentage is calculated after discounts are applied. If you select "Subtotal" or "Grand Total," the percentage is calculated before discounts. Choose the option that best fits your use case.
              </p>
            </Accordion>

            <Accordion title="Can I switch between Native and Custom checkout?">
              <p>
                Yes. Go to the Settings page and use the "Switch to Custom Checkout (Advanced)" or "Switch to Native Checkout (Recommended)" button under Checkout Integration. Switching modes doesn't affect your fee configurations; they're preserved and keep working under either mode.
              </p>
            </Accordion>

            <Accordion title="Do I need a credit card to start my free trial?">
              <p>
                No. Every new install gets a 14-day free trial automatically with no card required. You'll only need to add a payment method if and when you choose to subscribe.
              </p>
            </Accordion>

            <Accordion title="What happens when I cancel my subscription?">
              <p>
                Fees keep working through the end of the period you already paid for. BigCommerce doesn't issue a refund for the unused remainder, but you also won't be billed again after that date. You can resubscribe anytime to continue.
              </p>
            </Accordion>

            <Accordion title="Are fees included in tax calculations?">
              <p>
                Fees can be configured with a tax class. If you assign a tax class to a fee, BigCommerce will calculate tax on that fee based on your store's tax settings. If no tax class is assigned, the fee is not taxed.
              </p>
            </Accordion>
          </section>

          {/* Contact */}
          <section id="contact" style={{ ...styles.section, padding: '6rem 0', marginBottom: 0 }}>
            <div style={{
              ...styles.card,
              background: colors.indigo50,
              border: `1px solid ${colors.indigo100}`,
              textAlign: 'center',
              padding: '3rem',
            }}>
              <h3 style={{ ...styles.cardTitle, color: colors.indigo900, fontSize: '1.5rem', marginBottom: '1rem' }}>Need More Help?</h3>
              <p style={{ ...styles.cardText, color: colors.gray700 }}>
                If you have questions not covered here or need assistance with your setup, please contact our support team at{' '}
                <a href="mailto:hello@prangano.com" style={{ color: colors.indigo600, fontWeight: 600 }}>
                  hello@prangano.com
                </a>
              </p>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer style={styles.footer}>
          <div style={styles.footerContainer}>
            <img src="/logo-checkout-fees.png" alt="Checkout Fees" style={{ height: '40px' }} />
            <div style={{
              ...styles.footerLinks,
              ...(isMobile && { flexDirection: 'column', gap: '1rem' })
            }}>
              <a href="https://www.bigcommerce.com/apps/checkout-fees-by-prangano/" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>BigCommerce App Store</a>
              {!isMobile && <span style={styles.footerDot}>•</span>}
              <a href="/support" style={styles.footerLink}>Documentation</a>
              {!isMobile && <span style={styles.footerDot}>•</span>}
              <a href="/support#contact" style={styles.footerLink}>Support</a>
              {!isMobile && <span style={styles.footerDot}>•</span>}
              <a href="/privacy" style={styles.footerLink}>Privacy Policy</a>
              {!isMobile && <span style={styles.footerDot}>•</span>}
              <a href="/terms" style={styles.footerLink}>Terms of Service</a>
            </div>
            <p style={styles.footerCopyright}>© 2026 Checkout Fees. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}

export default SupportPage;
